import { Controller } from '@hotwired/stimulus'
import { variables } from './event_form_theme_controller'

// Live popup preview in the builder: rebuilds the popup JSON from the unsaved form and draws it with
// the real renderer (embed/popups.js) inside the stage. No rules, no storage, nothing counted; a
// form inside the popup renders from the schema printed on the page, so it is never fetched.
const WIDTHS = { desktop: '100%', mobile: '375px' }

export default class extends Controller {
  static targets = ['stage', 'schema', 'forms', 'device', 'sizeHint']

  connect() {
    this.base = JSON.parse(this.schemaTarget.textContent)
    this.formSchemas = JSON.parse(this.formsTarget.textContent)
    if (window.EightyEight) this.render()
    else document.addEventListener('eightyeight:ready', () => this.render(), { once: true })
  }

  disconnect() {
    this.cancelResize()
    if (this.preview) this.preview.destroy()
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
  }

  // Click too: the segmented controls change hidden inputs, which fire no input event.
  refresh() {
    cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(() => this.render())
  }

  replay() {
    this.render()
  }

  image(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) return
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = URL.createObjectURL(file)
    this.render()
  }

  device(event) {
    const width = WIDTHS[event.currentTarget.dataset.device] || '100%'
    const resizing = this.stageTarget.style.width !== width
    this.stageTarget.style.width = width
    this.deviceTargets.forEach((button) => {
      const active = button === event.currentTarget
      button.classList.toggle('active', active)
      button.setAttribute('aria-pressed', active)
    })
    this.cancelResize()
    if (!resizing) return this.render()
    // Draw once the stage has its new width: drawn mid-resize, the popup picks the desktop layout and a corner
    // popup grows in the corner before snapping to the phone's bottom sheet.
    if (this.preview) this.preview.destroy()
    this.preview = null
    const onEnd = (e) => { if (e.target === this.stageTarget && e.propertyName === 'width') done() }
    const timer = setTimeout(() => done(), 300)
    const done = () => { this.cancelResize(); this.render() }
    this.stageTarget.addEventListener('transitionend', onEnd)
    this.cancelResize = () => {
      clearTimeout(timer)
      this.stageTarget.removeEventListener('transitionend', onEnd)
      this.cancelResize = () => {}
    }
  }

  // Replaced by device() while a stage resize is pending.
  cancelResize() {}

  render() {
    const popup = this.popup()
    this.sizeHintTargets.forEach((hint) => { hint.hidden = hint.dataset.style !== popup.style })
    if (!window.EightyEight || !window.EightyEight.previewPopup) return
    if (this.preview) this.preview.destroy()
    this.preview = window.EightyEight.previewPopup(this.stageTarget, popup, { formSchema: popup.form ? this.formSchemas[popup.form] : null })
  }

  popup() {
    const form = this.element.querySelector('form.sp-form')
    if (!form) return this.base
    const value = (name) => {
      const field = form.querySelector(`[name="site_popup[${name}]"]`)
      return field && field.value.trim() ? field.value.trim() : null
    }
    const theme = {}
    form.querySelectorAll('input[data-theme-key][name]').forEach((input) => { theme[input.dataset.themeKey] = input.value })
    const checked = form.querySelector('input[name="site_popup[style]"]:checked')
    const style = checked ? checked.value : this.base.style
    const select = form.querySelector('[name="site_popup[event_form_id]"]')
    const option = select && select.selectedOptions[0]
    // A form that is off has no schema here and, like the live site, the preview leaves it out.
    const slug = option && this.formSchemas[option.dataset.slug] ? option.dataset.slug : null
    // An emptied editor still holds <div><br></div>; the server stores that as no text, so the preview does too.
    // Parsed inert, so nothing in unsaved markup loads or runs here.
    const body = value('body')
    const bodyText = body ? new DOMParser().parseFromString(body, 'text/html').body.textContent.trim() : ''
    return {
      ...this.base,
      style,
      headline: value('headline'),
      body: bodyText ? body : null,
      button_label: value('button_label'),
      button_url: value('button_url'),
      form: style === 'bar' ? null : slug,
      image_url: this.objectUrl || this.base.image_url,
      image_alt: value('image_alt'),
      css_variables: variables(theme),
      font_family: theme.font === 'google' ? theme.font_family : ''
    }
  }
}
